/**
 * US Time Zone Constants
 * Using IANA time zone identifiers
 */
export const TIME_ZONES = {
  UTC: 'UTC', // This time zone will be used as a fallback
  EASTERN: 'America/New_York',
  CENTRAL: 'America/Chicago',
  MOUNTAIN: 'America/Denver',
  PACIFIC: 'America/Los_Angeles',
  ALASKA: 'America/Anchorage',
  HAWAII: 'Pacific/Honolulu',
  ARIZONA: 'America/Phoenix', // Arizona doesn't observe DST
  INDIANA_EAST: 'America/Indiana/Indianapolis',
  INDIANA_WEST: 'America/Indiana/Knox',
};

/**
 * Complete mapping of US states to their primary time zones
 */
const STATE_TIME_ZONES = {
  // Eastern Time Zone
  AL: TIME_ZONES.EASTERN, // Alabama
  CT: TIME_ZONES.EASTERN, // Connecticut
  DE: TIME_ZONES.EASTERN, // Delaware
  FL: TIME_ZONES.EASTERN, // Florida (most of it)
  GA: TIME_ZONES.EASTERN, // Georgia
  IN: TIME_ZONES.INDIANA_EAST, // Indiana (eastern part)
  KY: TIME_ZONES.EASTERN, // Kentucky (eastern part)
  ME: TIME_ZONES.EASTERN, // Maine
  MD: TIME_ZONES.EASTERN, // Maryland
  MA: TIME_ZONES.EASTERN, // Massachusetts
  MI: TIME_ZONES.EASTERN, // Michigan (eastern part)
  NH: TIME_ZONES.EASTERN, // New Hampshire
  NJ: TIME_ZONES.EASTERN, // New Jersey
  NY: TIME_ZONES.EASTERN, // New York
  NC: TIME_ZONES.EASTERN, // North Carolina
  OH: TIME_ZONES.EASTERN, // Ohio
  PA: TIME_ZONES.EASTERN, // Pennsylvania
  RI: TIME_ZONES.EASTERN, // Rhode Island
  SC: TIME_ZONES.EASTERN, // South Carolina
  TN: TIME_ZONES.EASTERN, // Tennessee (eastern part)
  VT: TIME_ZONES.EASTERN, // Vermont
  VA: TIME_ZONES.EASTERN, // Virginia
  WV: TIME_ZONES.EASTERN, // West Virginia

  // Central Time Zone
  AR: TIME_ZONES.CENTRAL, // Arkansas
  IL: TIME_ZONES.CENTRAL, // Illinois
  IA: TIME_ZONES.CENTRAL, // Iowa
  KS: TIME_ZONES.CENTRAL, // Kansas (most of it)
  LA: TIME_ZONES.CENTRAL, // Louisiana
  MN: TIME_ZONES.CENTRAL, // Minnesota
  MS: TIME_ZONES.CENTRAL, // Mississippi
  MO: TIME_ZONES.CENTRAL, // Missouri
  NE: TIME_ZONES.CENTRAL, // Nebraska (eastern part)
  ND: TIME_ZONES.CENTRAL, // North Dakota (eastern part)
  OK: TIME_ZONES.CENTRAL, // Oklahoma
  SD: TIME_ZONES.CENTRAL, // South Dakota (eastern part)
  TX: TIME_ZONES.CENTRAL, // Texas (most of it)
  WI: TIME_ZONES.CENTRAL, // Wisconsin

  // Mountain Time Zone
  CO: TIME_ZONES.MOUNTAIN, // Colorado
  ID: TIME_ZONES.MOUNTAIN, // Idaho (southern part)
  MT: TIME_ZONES.MOUNTAIN, // Montana
  NV: TIME_ZONES.MOUNTAIN, // Nevada (most of it)
  NM: TIME_ZONES.MOUNTAIN, // New Mexico
  UT: TIME_ZONES.MOUNTAIN, // Utah
  WY: TIME_ZONES.MOUNTAIN, // Wyoming

  // Pacific Time Zone
  CA: TIME_ZONES.PACIFIC, // California
  OR: TIME_ZONES.PACIFIC, // Oregon
  WA: TIME_ZONES.PACIFIC, // Washington

  // Special Cases
  AZ: TIME_ZONES.ARIZONA, // Arizona (no DST)
  AK: TIME_ZONES.ALASKA, // Alaska
  HI: TIME_ZONES.HAWAII, // Hawaii

  // US Territories (bonus)
  PR: TIME_ZONES.EASTERN, // Puerto Rico
  VI: TIME_ZONES.EASTERN, // US Virgin Islands
  GU: 'Pacific/Guam', // Guam
  AS: 'Pacific/Pago_Pago', // American Samoa
  MP: 'Pacific/Saipan', // Northern Mariana Islands
};

/**
 * Time Zone Service
 */
export class TimeZoneService {
  /**
   * Get time zone for a US state
   * @param {string} state - Two-letter state code (e.g., 'CA', 'NY')
   * @returns {string} IANA time zone identifier
   */
  getTimeZoneForState(state) {
    if (!state || typeof state !== 'string') {
      return TIME_ZONES.UTC; // Default fallback
    }

    const upperState = state.toUpperCase();
    return STATE_TIME_ZONES[upperState] || TIME_ZONES.UTC;
  }

  /**
   * Get current time in a specific time zone
   * @param {string} timeZone - IANA time zone identifier
   * @param {Object} options - Formatting options
   * @returns {string} Formatted time string
   */
  getCurrentTimeInTimeZone(timeZone, options = {}) {
    const defaultOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timeZone,
    };

    const formatOptions = { ...defaultOptions, ...options };

    try {
      return new Intl.DateTimeFormat('en-US', formatOptions).format(new Date());
    } catch (error) {
      console.error('Invalid time zone:', timeZone, error);
      return new Intl.DateTimeFormat('en-US', {
        ...formatOptions,
        timeZone: TIME_ZONES.EASTERN,
      }).format(new Date());
    }
  }

  /**
   * Get current time for a US state
   * @param {string} state - Two-letter state code
   * @param {Object} options - Formatting options
   * @returns {string} Formatted time string
   */
  getCurrentTimeForState(state, options = {}) {
    const timeZone = this.getTimeZoneForState(state);
    return this.getCurrentTimeInTimeZone(timeZone, options);
  }

  /**
   * Check if it's business hours in a specific time zone
   * @param {string} timeZone - IANA time zone identifier
   * @param {Object} businessHours - Business hours configuration
   * @returns {boolean}
   */
  isBusinessHours(timeZone, businessHours = {}) {
    const { startHour = 9, endHour = 17, weekdaysOnly = true } = businessHours;

    try {
      const now = new Date();
      const localTime = new Date(now.toLocaleString('en-US', { timeZone }));
      const currentHour = localTime.getHours();
      const currentDay = localTime.getDay(); // 0 = Sunday, 6 = Saturday

      const isWeekday = currentDay >= 1 && currentDay <= 5;
      const isDuringBusinessHours = currentHour >= startHour && currentHour < endHour;

      return weekdaysOnly ? isWeekday && isDuringBusinessHours : isDuringBusinessHours;
    } catch (error) {
      console.error('Error checking business hours:', error);
      return false;
    }
  }

  /**
   * Check if it's business hours for a US state
   * @param {string} state - Two-letter state code
   * @param {Object} businessHours - Business hours configuration
   * @returns {boolean}
   */
  isBusinessHoursForState(state, businessHours = {}) {
    const timeZone = this.getTimeZoneForState(state);
    return this.isBusinessHours(timeZone, businessHours);
  }

  /**
   * Get all supported time zones
   * @returns {Object} Time zone constants
   */
  getAllTimeZones() {
    return { ...TIME_ZONES };
  }

  /**
   * Get time zone display name
   * @param {string} timeZone - IANA time zone identifier
   * @returns {string} Human-readable time zone name
   */
  getTimeZoneDisplayName(timeZone) {
    const timeZoneNames = {
      [TIME_ZONES.EASTERN]: 'Eastern Time',
      [TIME_ZONES.CENTRAL]: 'Central Time',
      [TIME_ZONES.MOUNTAIN]: 'Mountain Time',
      [TIME_ZONES.PACIFIC]: 'Pacific Time',
      [TIME_ZONES.ALASKA]: 'Alaska Time',
      [TIME_ZONES.HAWAII]: 'Hawaii Time',
      [TIME_ZONES.ARIZONA]: 'Arizona Time',
      [TIME_ZONES.INDIANA_EAST]: 'Eastern Time (Indiana)',
      [TIME_ZONES.INDIANA_WEST]: 'Central Time (Indiana)',
      [TIME_ZONES.UTC]: 'Coordinated Universal Time',
    };

    return timeZoneNames[timeZone] || 'Undiscovered Time Zone';
  }

  /**
   * Get operating hours for a time zone. Defaults to 8am - 5pm in whichever time zone is provided.
   * @param {string} timeZone
   * @returns {Date[]}
   */
  getOperatingHoursForTimeZone(timeZone) {
    /**
     * Create a Date object for a specific hour in the target time zone
     * @param {string} description - what to do (for documentation)
     * @param {number} hour - hour to create the date for
     */
    const createTimeInZone = (description, hour) => {
      const now = new Date();

      const targetTime = new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(now);

      const [targetYear, targetMonth, targetDay] = targetTime.split('-');

      // Create the final date object
      return new Date(
        `${targetYear}-${targetMonth}-${targetDay}T${String(hour).padStart(2, '0')}:00:00`
      );
    };

    // 8am
    const military8am = 8;
    const openingHour = createTimeInZone('open', military8am);

    // 5pm
    const military5pm = 12 + 5;
    const closingHour = createTimeInZone('close', military5pm);

    return [openingHour, closingHour];
  }
}

const timeZoneService = new TimeZoneService();
export default timeZoneService;
